import { useState, useEffect } from "react"
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from "react-router-dom"
import { shipmentReturnService } from "../../services/shipmentReturnService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Package, Plus, FileDown, RefreshCw } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { formatAuditUser } from '../../utils/userDisplay.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';


const ReturnList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const [returns, setReturns] = useState([])
  const [shipments, setShipments] = useState({})
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [statusFilter, setStatusFilter] = useState("")
  const [search, setSearch] = useState("")
  const [openMenuId, setOpenMenuId] = useState(null)

  useEffect(() => {
    fetchReturns()
  }, [page, statusFilter, search])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId])

  const fetchReturns = async () => {
    try {
      setLoading(true)
      const filters = {
        ...(statusFilter ? { return_status: statusFilter } : {}),
        ...(search ? { search } : {}),
      }
      const response = await shipmentReturnService.getAllReturns(page, 10, filters)

      const returnsData = response.data.returns || response.data.data || []
      setReturns((prev) => mergeLoadedPage(page, returnsData, prev))

      const pagination = response.data.pagination || {}
      setTotalPages(pagination.pages || 1)
    } catch (error) {
      console.error("Error fetching returns:", error)
      Alert.error(error.response?.data?.error || "Failed to fetch returns")
      setReturns([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      open: "bg-blue-100 text-blue-800",
      close: "bg-green-100 text-green-800",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  const canEditReturn = (returnStatus) => {
    const allowedRoles = ["System Admin", "Admin", "Accountant"]
    return allowedRoles.includes(user?.role)
  }

  const isShipmentClosed = (shipmentData) => {
    return shipmentData?.status?.toLowerCase() === "closed"
  }

  const canShowEditIcon = (returnStatus, shipmentData) => {
    return canEditReturn(returnStatus) && !isShipmentClosed(shipmentData)
  }

  const toggleMenu = (returnId) => {
    setOpenMenuId(openMenuId === returnId ? null : returnId)
  }

  const hasActiveFilters = statusFilter || search;

  const clearFilters = () => {
    setStatusFilter("")
    setSearch("")
    setPage(1)
  }

  return (
    <DashboardLayout>
              <div className="max-w-full mx-auto">
                <ListPageHeader
                  title={t('pages.return.title')}
                  className="mb-6 sm:mb-4"
                >
                  <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate("/returns/add")}>
                    Return Request
                  </Button>
                </ListPageHeader>

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <SearchSubmitInput
                      placeholder="Search by return ID, shipment ID..."
                      value={search}
                      onSearch={(value) => {
                        setSearch(value)
                        setPage(1)
                      }}
                      inputClassName="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 transition-all"
                    />
                  </div>

                  <div>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value)
                        setPage(1)
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 transition-all"
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="open">Open</option>
                      <option value="close">Close</option>
                    </select>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="reset" icon={RefreshCw} onClick={fetchReturns} fullWidth>
                      Refresh
                    </Button>
                  </div>
                </ListFilterPanel>

                {loading && page === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading return requests..." />
                  </div>
                ) : returns.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="text-6xl mb-4">🔍</div>
                    <p className="text-gray-600 text-lg font-semibold">No return requests found</p>
                    <Button variant="primary" onClick={() => navigate("/returns/add")} className="mt-4">
                      Create First Return Request
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden lg:block bg-white rounded-xl shadow-sm">
                      <div className="w-full">
                        <table className="w-full table-fixed border-collapse">
                          <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                            <tr className="bg-gray-50 text-gray-700">
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase">Return ID</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase">Shipment ID</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase">Reason</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase">{t('common.createdBy')}</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase text-center">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {returns.map((ret, index) => (
                              <tr
                                key={ret.id || ret.return_id}
                                className={`hover:bg-gray-50 relative transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                              >
                                <td className="px-5 py-4">
                                  <Button variant="link" onClick={() => navigate(`/returns/view/${ret.return_id}`)} className={listIdLinkClass}>
                                    {ret.return_id}
                                  </Button>
                                </td>
                                <td className="px-5 py-4 text-sm">
                                  <Button variant="link" onClick={() => navigate(`/shipments/view/${ret.shipment_id}`)} className={listIdLinkClass}>
                                    {ret.shipment_id}
                                  </Button>
                                </td>
                                <td className="px-5 py-4 text-sm text-gray-900 max-w-xs">
                                  <div className="truncate font-medium text-secondary hover:text-secondary hover:underline" title={ret.reason_for_return}>
                                    {ret.reason_for_return?.substring(0, 50)}
                                    {ret.reason_for_return?.length > 50 ? "..." : ""}
                                  </div>
                                </td>
                                <td className="px-5 py-4">
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(ret.return_status)}`}
                                  >
                                    {ret.return_status}
                                  </span>
                                </td>
                                <td className="px-5 py-4 text-sm text-gray-700 font-semibold">{formatAuditUser(ret, 'created_by')}</td>
                                <td className="px-5 py-4 text-sm text-gray-700 font-semibold">
                                  {ret.created_at
                                    ? new Date(ret.created_at).toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })
                                    : "N/A"}
                                </td>
                                <td className="px-5 py-4">
                                  <RowActionsMenu
                                    className="relative menu-container inline-block"
                                    isOpen={openMenuId === ret.return_id}
                                    onToggle={() => setOpenMenuId(openMenuId === ret.return_id ? null : ret.return_id)}
                                    onClose={() => setOpenMenuId(null)}
                                    onView={() => navigate(`/returns/view/${ret.return_id}`)}
                                    onEdit={
                                      canShowEditIcon(ret.return_status, ret.shipmentData) && ret.return_status === 'pending'
                                        ? () => navigate(`/returns/edit/${ret.return_id}`)
                                        : undefined
                                    }
                                    editLabel="Manage Return"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                    </div>

                    <div className="lg:hidden space-y-3">
                      {returns.map((ret) => (
                        <div key={ret.id || ret.return_id} className="bg-white rounded-xl shadow-md p-4">
                          <div className="flex justify-between items-start gap-2 mb-3">
                            <div className="min-w-0">
                              <Button variant="link" onClick={() => navigate(`/returns/view/${ret.return_id}`)} className={`${listIdLinkMobileClass} p-0`}>
                                {ret.return_id}
                              </Button>
                              <Button variant="link" onClick={() => navigate(`/shipments/view/${ret.shipment_id}`)} className={`${listIdLinkMobileClass} block mt-1 p-0`}>
                                Shipment: {ret.shipment_id}
                              </Button>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold uppercase whitespace-nowrap ${getStatusBadgeColor(ret.return_status)}`}
                              >
                                {ret.return_status}
                              </span>
                              <RowActionsMenu
                                className="relative menu-container"
                                isOpen={openMenuId === ret.return_id}
                                onToggle={() => setOpenMenuId(openMenuId === ret.return_id ? null : ret.return_id)}
                                onClose={() => setOpenMenuId(null)}
                                onView={() => navigate(`/returns/view/${ret.return_id}`)}
                                onEdit={
                                  canShowEditIcon(ret.return_status, ret.shipmentData) && ret.return_status === 'pending'
                                    ? () => navigate(`/returns/edit/${ret.return_id}`)
                                    : undefined
                                }
                                editLabel="Manage Return"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                            <div className="col-span-2">
                              <span className="text-gray-500">Reason:</span>
                              <p className="text-gray-900 font-medium line-clamp-2">{ret.reason_for_return}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Created By:</span>
                              <p className="text-gray-900 font-semibold">{formatAuditUser(ret, 'created_by')}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="text-gray-900">
                                {ret.created_at
                                  ? new Date(ret.created_at).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}

                    </div>
                    <ListPagination
                      currentPage={page}
                      totalPages={totalPages}
                      loading={loading && page > 1}
                      onNext={() => { setLoading(true); setPage((prev) => Math.min(totalPages, prev + 1)); }}
                    />
                  </>
                )}
              </div>
    </DashboardLayout>
  )
}

export default ReturnList;
